<template>
  <!-- XOL-586 (Delta 1, item 4): the Re-send recipient picker. A Re-send of step 2's / step 5's
       approval e-mail does not send at once: this dialog lists the addresses the e-mail would
       go to NOW, says which of them already have this version, and lets the user choose. The
       addresses that did not receive it (new ones, corrected ones, ones that failed) are
       ticked and highlighted; the ones that did are listed, unticked, in case they need
       another copy; an invalid address is listed with its reason and cannot be ticked.

       Presentational. The parent (RequestMixins: openResendPicker / loadResendPreview /
       submitResend) owns the data and the requests; this component owns the ticked set and
       emits `send` (the ticked addresses, lower-cased: the server's keys), `retry` and
       `close`. The first Send of an e-mail never opens it.

       The list is GET /actions/{id}/notify/preview. The server only ever intersects the
       ticked addresses with the live recipients: an address it no longer has comes back as 409
       preview_stale, which the parent shows through `stale` and answers by reading the list
       again. -->
  <el-dialog
    :visible="visible"
    :title="title"
    append-to-body
    :width="isMediumOrLess ? '92%' : '640px'"
    top="8vh"
    custom-class="xol-resend-dialog xol-info-dialog"
    :close-on-click-modal="false"
    @open="onOpen"
    @opened="onOpened"
    @close="onDialogClose"
    @closed="onClosed">

    <!-- Delta 4: only the header is new (the shared InfoDialogHeader); "Last sent …" is its subtitle. -->
    <info-dialog-header slot="title"
      icon="fa-paper-plane"
      :title="title"
      :subtitle="lastSent"
      :compact="isMediumOrLess" />

    <div class="resend" ref="body" tabindex="-1"
      :class="{ 'is-empty': !preview && !error, 'is-narrow': isMediumOrLess }"
      v-loading="loading" :element-loading-text="text('requestMail.resend.loading')">

      <div v-if="error" class="resend__error">
        <el-alert type="error" show-icon :closable="false"
          :title="errorTitle" :description="error === 'generic' ? text('requestMail.resend.error.body') : ''" />
        <el-button v-if="error === 'generic'" size="small" class="resend__retry" :disabled="loading" @click="$emit('retry')">{{text('requestMail.resend.error.retry')}}</el-button>
      </div>

      <template v-if="preview && !error">

        <p class="resend__intro" tabindex="-1" ref="intro">{{intro}}</p>

        <el-alert v-if="staleView" ref="stale" tabindex="-1" class="resend__stale"
          type="warning" show-icon :closable="false"
          :title="staleView.title" :description="staleView.body" />

        <el-alert v-if="nobody" class="resend__nobody"
          type="warning" show-icon :closable="false"
          :title="text('requestMail.resend.nobody.title', { owner: ownerName })"
          :description="text('requestMail.resend.nobody.body', { owner: ownerName })" />

        <template v-else>

          <el-alert v-if="allReceived" class="resend__all-received"
            type="info" show-icon :closable="false"
            :title="text('requestMail.resend.allReceived.title')"
            :description="text('requestMail.resend.allReceived.body')" />

          <div class="resend__toolbar">
            <el-button type="text" size="mini" :disabled="sending" @click="selectAll">{{text('requestMail.resend.selectAll')}}</el-button>
            <el-button type="text" size="mini" :disabled="sending" @click="selectNone">{{text('requestMail.resend.selectNone')}}</el-button>
            <span class="resend__spacer"></span>
            <span class="resend__count" :id="countId" aria-live="polite">{{countText}}</span>
          </div>

          <div v-for="g in groups" :key="g.key" class="resend-group" :class="'is-' + g.key">
            <div class="resend-group__head">
              <el-checkbox v-if="g.key !== 'invalid'"
                :value="groupAll(g)" :indeterminate="groupSome(g)" :disabled="sending"
                :aria-label="text('requestMail.resend.group.aria', { group: g.title })"
                @change="toggleGroup(g, $event)"><b>{{g.title}}</b></el-checkbox>
              <b v-else>{{g.title}}</b>
              <small class="text-muted resend-group__help" :class="{ 'is-indented': g.key !== 'invalid' }">{{g.help}}</small>
            </div>

            <el-checkbox-group v-model="selected" class="resend-list" :disabled="sending" :aria-label="g.title">
              <div v-for="row in g.rows" :key="row.key" class="resend-row" :class="'is-' + row.state">
                <div class="resend-row__line">
                  <el-checkbox :label="row.key" :disabled="!row.selectable || sending">
                    <span class="resend-row__email">{{row.email}}</span>
                    <span class="visually-hidden">: {{row.tag.text}}. {{row.meta}}</span>
                  </el-checkbox>
                  <el-tag size="mini" effect="light" :type="row.tag.type" class="resend-row__tag"><i class="fa" :class="row.tag.icon" aria-hidden="true"></i><span>{{row.tag.text}}</span></el-tag>
                </div>
                <small class="resend-row__meta" aria-hidden="true">{{row.meta}}</small>
              </div>
            </el-checkbox-group>
          </div>

        </template>

        <el-alert v-if="skippedTexts.length > 0" class="resend__skipped"
          type="info" :title="text('requestMail.skipped.title')" show-icon :closable="false">
          <ul class="resend__skipped-list">
            <li v-for="(line, i) in skippedTexts" :key="i">{{line}}</li>
          </ul>
        </el-alert>

      </template>
    </div>

    <div slot="footer" class="resend__footer" :class="{ 'is-narrow': isMediumOrLess }">
      <el-button size="small" @click="requestClose">{{text('requestMail.resend.cancel')}}</el-button>
      <el-button size="small" type="primary" class="resend__send"
        :loading="sending"
        :disabled="sendDisabled"
        :aria-describedby="countId"
        @click="send">{{sendText}}</el-button>
    </div>

  </el-dialog>
</template>

<script>
import DateMixins from '@/mixins/DateMixins'
import InfoDialogHeader from '@/components/requests/InfoDialogHeader'
import { activityText, defaultResendSelection, resendRows, resendTag, resendMeta, resendStaleText, skippedLines } from '@/lib/requestActivity'

export default {
  name: 'resend-recipients-dialog',

  mixins: [DateMixins],

  components: {
    InfoDialogHeader
  },

  props: {
    // The parent owns it and clears it on `close`.
    visible: {
      type: Boolean,
      default: false
    },

    // The GET .../notify/preview body: { version, status, last_batch, recipients, skipped, ... }.
    // Null until the first answer (and after an error).
    preview: {
      type: Object,
      default: null
    },

    // The preview is being read. The list (if any) stays under the spinner.
    loading: {
      type: Boolean,
      default: false
    },

    // null | 'generic' | 'notFound' | 'forbidden'
    error: {
      type: String,
      default: null
    },

    // The POST is running: every control disables, the primary shows its spinner.
    sending: {
      type: Boolean,
      default: false
    },

    // After a 409 preview_stale: { reason: 'content_changed' | 'recipients_changed', unknown }.
    stale: {
      type: Object,
      default: null
    },

    // The step's approval status and whether its e-mail was sent before: the title while the
    // preview is still loading.
    status: {
      type: String,
      default: null
    },

    hadMail: {
      type: Boolean,
      default: true
    },

    // The element that opened the dialog; focus returns to it when the dialog has closed.
    // Any DOM element, or null (Vue 2 prop validation: `null` accepts every type).
    opener: {
      type: null,
      default: null
    },

    // The owner organisation (named when nobody gets the e-mail) and the Tower Co (the 403).
    owner: {
      type: String,
      default: null
    },

    towerCo: {
      type: String,
      default: null
    },

    // The request type, for the "not e-mailed" lines.
    rtx: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      // The ticked addresses (keys). Starts as the suggestion for this list and is reset to it
      // every time a new list arrives.
      selected: defaultResendSelection(this.preview),
      opened: false
    }
  },

  computed: {
    countId () {
      return 'resend-count-' + this._uid
    },

    rows () {
      return resendRows(this.preview)
    },

    selectableKeys () {
      return this.rows.filter(function (r) { return r.selectable }).map(function (r) { return r.key })
    },

    // The ticked addresses that can be mailed, in list order. An invalid address is never in it,
    // whatever `selected` holds.
    selectedKeys () {
      var ticked = this.selected

      return this.selectableKeys.filter(function (k) { return ticked.indexOf(k) !== -1 })
    },

    groups () {
      var self = this
      var defs = [
        { key: 'pending', title: 'requestMail.resend.group.pending', help: 'requestMail.resend.group.pending.help' },
        { key: 'done', title: 'requestMail.resend.group.done', help: 'requestMail.resend.group.done.help' },
        { key: 'invalid', title: 'requestMail.resend.group.invalid', help: 'requestMail.resend.group.invalid.help' }
      ]

      return defs.map(function (d) {
        var rows = self.rows.filter(function (r) { return r.group === d.key }).map(function (r) {
          return Object.assign({}, r, { tag: resendTag(r.state), meta: resendMeta(r, { formatDateTime: self.formatDateTime }) })
        })

        return { key: d.key, title: activityText(d.title, { n: rows.length }), help: activityText(d.help), rows: rows }
      }).filter(function (g) { return g.rows.length > 0 })
    },

    // Nobody receives the e-mail now (the resolver found no one).
    nobody () {
      return !!this.preview && this.rows.length === 0
    },

    // Everyone who can be mailed already has this version: nothing is ticked.
    allReceived () {
      return this.rows.length > 0 &&
        !this.rows.some(function (r) { return r.group === 'pending' }) &&
        this.rows.some(function (r) { return r.state === 'received' }) &&
        this.rows.every(function (r) { return r.state === 'received' || r.state === 'invalid' })
    },

    // "Re-send" while an earlier send of this version exists; "Send" when none does (the
    // flush changed the e-mail, so nothing has gone out for this version yet).
    isResend () {
      return this.preview ? !!this.preview.last_batch : this.hadMail
    },

    approvalStatus () {
      return (this.preview && this.preview.status) || this.status || 'Approval'
    },

    title () {
      return activityText(this.isResend ? 'requestMail.resend.title' : 'requestMail.resend.titleFirst', { status: this.approvalStatus })
    },

    intro () {
      return activityText(this.isResend ? 'requestMail.resend.intro' : 'requestMail.resend.introFirst', { status: this.approvalStatus })
    },

    lastSent () {
      var b = this.preview ? this.preview.last_batch : null

      if (!b) {
        return null
      }

      return activityText('requestMail.resend.lastSent', {
        datetime: b.sent_at ? this.formatDateTime(b.sent_at) : '',
        name: b.triggered_by && b.triggered_by !== 'system' ? b.triggered_by : null,
        subset: !!b.subset
      })
    },

    staleView () {
      return this.stale && this.preview ? resendStaleText(this.stale) : null
    },

    skippedTexts () {
      return skippedLines(this.preview ? this.preview.skipped : [], { rtx: this.rtx || 'request' })
    },

    ownerName () {
      return this.owner || 'the telco'
    },

    errorTitle () {
      if (this.error === 'forbidden') {
        return activityText('requestMail.notify.forbidden', { towerCo: this.towerCo || 'the Tower Co' })
      }

      return activityText(this.error === 'notFound' ? 'requestMail.resend.error.notFound' : 'requestMail.resend.error.title')
    },

    countText () {
      return activityText('requestMail.resend.count', { k: this.selectedKeys.length, n: this.selectableKeys.length })
    },

    sendDisabled () {
      return this.selectedKeys.length === 0 || this.loading || !!this.error || this.sending || !this.preview
    },

    sendText () {
      var k = this.selectedKeys.length

      if (this.sending) {
        return activityText('requestMail.send.sending')
      }
      if (k === 0) {
        return activityText(this.isResend ? 'requestMail.resend.sendNone' : 'requestMail.resend.sendNoneFirst')
      }

      return activityText(this.isResend ? 'requestMail.resend.send' : 'requestMail.resend.sendFirst', { k: k })
    }
  },

  watch: {
    // A new list (the first, a retry, or the refresh after a stale refusal): the suggestion
    // again, and focus on what to read first.
    preview (now) {
      this.selected = defaultResendSelection(now)
      this.focusFirst()
    }
  },

  methods: {
    text (key, params) {
      return activityText(key, params)
    },

    groupAll (g) {
      var ticked = this.selected

      return g.rows.length > 0 && g.rows.every(function (r) { return ticked.indexOf(r.key) !== -1 })
    },

    groupSome (g) {
      var ticked = this.selected

      return !this.groupAll(g) && g.rows.some(function (r) { return ticked.indexOf(r.key) !== -1 })
    },

    // The group header's checkbox ticks or clears that group's rows and leaves the others.
    toggleGroup (g, on) {
      var keys = g.rows.filter(function (r) { return r.selectable }).map(function (r) { return r.key })
      var rest = this.selected.filter(function (k) { return keys.indexOf(k) === -1 })

      this.selected = on ? rest.concat(keys) : rest
    },

    // Every address that can be mailed. An invalid one is not selectable.
    selectAll () {
      this.selected = this.selectableKeys.slice()
    },

    selectNone () {
      this.selected = []
    },

    send () {
      if (this.sendDisabled) {
        return
      }

      this.$emit('send', this.selectedKeys.slice())
    },

    requestClose () {
      this.$emit('close')
    },

    // After a stale refusal the notice gets focus (it is role="alert", so it is announced);
    // otherwise the intro. While the first list loads, the body.
    focusFirst () {
      var self = this

      if (!this.opened) {
        return
      }

      this.$nextTick(function () {
        var alert = self.staleView && self.$refs.stale ? self.$refs.stale.$el : null
        var el = alert || self.$refs.intro || self.$refs.body

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },

    onOpen () {
      this.selected = defaultResendSelection(this.preview)
    },

    onOpened () {
      this.opened = true
      this.focusFirst()
    },

    // Element closed itself (the ×, Esc): hand it to the parent. A close the parent started
    // (`visible` already false) is not announced twice.
    onDialogClose () {
      if (this.visible) {
        this.$emit('close')
      }
    },

    // The dialog has closed: focus goes back to what opened it.
    onClosed () {
      var opener = this.opener

      this.opened = false

      if (opener && typeof opener.focus === 'function' && typeof document !== 'undefined' && document.body.contains(opener)) {
        opener.focus()
      }
    }
  }
}
</script>

<style scoped>
.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

.resend { outline: none; }
.resend.is-empty { min-height: 200px; }
.resend__retry { margin-top: 8px; }
.resend__intro { margin: 0 0 4px; font-size: 13px; line-height: 1.5; color: #606266; outline: none; }
.resend__stale, .resend__nobody, .resend__all-received { margin: 10px 0; }
.resend__stale { outline: none; }
.resend__toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin: 10px 0 6px; }
.resend__spacer { flex: 1; }
.resend__count { font-size: 12px; color: #606266; }
.resend__skipped { margin-top: 4px; }
.resend__skipped-list { margin: 4px 0 0; padding-left: 18px; font-size: 13px; }
.resend__footer { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }

/* The highlight: addresses that did not get this version. Never colour alone: the group has a
   heading and each row a text tag. */
.resend-group { padding: 8px 12px; margin-bottom: 10px; border-radius: 4px; }
.resend-group.is-pending { background: #FDF6EC; border-left: 3px solid #E6A23C; }
.resend-group.is-done { border: 1px solid #EBEEF5; }
.resend-group.is-invalid { background: #F5F7FA; margin-bottom: 0; }
.resend-group__head b { font-size: 13px; color: #303133; }
.resend-group__help { display: block; font-size: 12px; }
.resend-group__help.is-indented { padding-left: 24px; }

.resend-row { padding: 6px 0; border-top: 1px solid #EBEEF5; }
.resend-row__line { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.resend-row__email { font-size: 13px; color: #303133; }
.resend-row__meta { display: block; padding-left: 24px; font-size: 12px; color: #909399; }
.resend-list ::v-deep .el-checkbox { display: flex; align-items: flex-start; white-space: normal; margin-right: 0; min-width: 0; }
.resend-list ::v-deep .el-checkbox__label { white-space: normal; word-break: break-all; line-height: 1.5; }
.resend-list ::v-deep .el-checkbox__input { margin-top: 2px; }
.resend-group__head ::v-deep .el-checkbox__label { white-space: normal; }
.resend-row__tag { flex: none; }
.resend-row__tag .fa { margin-right: 5px; }

/* Phone width: the tag drops under the address; the footer stacks, Cancel first, full width. */
.resend.is-narrow .resend-row__line { flex-direction: column; gap: 2px; }
.resend.is-narrow .resend-row__tag { margin-left: 24px; }
.resend__footer.is-narrow { flex-direction: column; align-items: stretch; }
.resend__footer.is-narrow .el-button { width: 100%; margin-left: 0; }
</style>

<style>
/* Element's dialog body breaks words anywhere and has no height limit: the list scrolls inside the
   dialog, and text breaks at spaces. Not scoped: `.xol-resend-dialog` is Element's own element. */
.xol-resend-dialog .el-dialog__body { word-break: normal; overflow-wrap: break-word; max-height: 64vh; overflow-y: auto; }
/* It also carries the shared xol-info-dialog shell (Delta 4), whose body is 70vh: keep this one's 64vh. */
.xol-resend-dialog.xol-info-dialog .el-dialog__body { max-height: 64vh; }
</style>
